// SOVRANOVA — web push subscribe helper (Task 5, Sprint 2)
// ---------------------------------------------------------------------------
// No UI wires this up yet beyond the Settings "Notifications" toggle (which
// calls subscribeToPush/unsubscribeFromPush directly) — actual push sending
// is server-side only this sprint (backend/services/push/pushService.js),
// gated on VAPID_PUBLIC_KEY && VAPID_PRIVATE_KEY being set. If the backend
// isn't configured, GET /api/push/vapid-key 503s and these helpers resolve to
// a harmless { ok: false } rather than throwing into the UI.
import { api } from './api'

function base64UrlToUint8Array(base64Url) {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

export function isPushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
}

/**
 * Subscribes this browser to push and registers it with the backend.
 * Never throws — every failure mode (unsupported browser, denied permission,
 * backend not configured) resolves to { ok: false, reason }.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  let vapid
  try {
    vapid = await api.get('/push/vapid-key')
  } catch (_) {
    return { ok: false, reason: 'unavailable' }
  }
  if (!vapid || !vapid.key) return { ok: false, reason: 'unavailable' }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { ok: false, reason: 'denied' }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(vapid.key),
    })

    await api.post('/push/subscribe', { subscription: subscription.toJSON() })
    return { ok: true }
  } catch (_) {
    return { ok: false, reason: 'failed' }
  }
}

/**
 * Unsubscribes this browser from push (both the browser's own subscription
 * and the backend record). Never throws.
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) await subscription.unsubscribe()
  } catch (_) {
    // Best-effort on the browser side — still tell the backend to drop it.
  }

  try {
    await api.del('/push/subscribe')
    return { ok: true }
  } catch (_) {
    return { ok: false, reason: 'failed' }
  }
}
